import { DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { httpsCallable } from 'firebase/functions';

import { FIREBASE_HANDLES } from '../firebase/firebase.providers';
import { OrgService } from './org.service';

/** How often the app checks in while it is open in a visible tab. */
export const CHECK_IN_INTERVAL_MS = 3 * 60 * 1000;

/**
 * Tells the server that someone who can record is using GHOSTS in the active
 * org, so the org's ready device runs while people are here and stops about
 * 15 minutes after the last of them leaves (spec
 * 2026-11-01-ready-devices-while-active §4).
 *
 * Checks in at once when the app opens, when the tab is shown and when the
 * active org changes, then every 3 minutes while the tab stays visible.
 * Never for a viewer, and never for an internal "Act as" tab: looking at a
 * customer's org must not keep their device running. A failed check-in is
 * only logged; the next tick tries again.
 */
@Injectable({ providedIn: 'root' })
export class OrgActivityService {
  private readonly org = inject(OrgService);
  private readonly checkIn = httpsCallable<{ orgId: string }, { counted: boolean }>(
    inject(FIREBASE_HANDLES).functions,
    'checkInOrgActivity',
  );
  private readonly visible = signal(document.visibilityState === 'visible');
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    const onVisibility = () => this.visible.set(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);

    effect(() => {
      const orgId = this.org.activeOrgId();
      const counts =
        orgId !== null &&
        this.org.tabOrgId() === null &&
        this.org.can('recordings:write') &&
        this.visible();
      this.stop();
      if (!counts) return;
      void this.send(orgId);
      this.timer = setInterval(() => void this.send(orgId), CHECK_IN_INTERVAL_MS);
    });

    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('visibilitychange', onVisibility);
      this.stop();
    });
  }

  private stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  private async send(orgId: string): Promise<void> {
    try {
      await this.checkIn({ orgId });
    } catch (err) {
      console.warn('Activity check-in failed; retrying at the next tick', err);
    }
  }
}
